import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faUser, faSearch } from '@fortawesome/free-solid-svg-icons';
import { Helmet } from 'react-helmet-async';

// Blog sayfasının verisi - gerçek projede bu API'den gelebilir
const blogPosts = [
  {
    id: 'secure-access-service-edge',
    title: 'What is Secure Access Service Edge (SASE)?',
    excerpt: 'In today\'s rapidly evolving digital landscape, businesses are more connected than ever, but with this connectivity comes increased security risks. Secure Access Service Edge (SASE) offers a comprehensive solution.',
    author: 'Doğukan Yaylacı',
    date: 'September 24, 2024',
    category: 'Security',
    image: 'https://via.placeholder.com/800x400?text=SASE',
    tags: ['Security', 'Networking', 'Cloud']
  },
  {
    id: 'ai-in-content-creation',
    title: 'The Role of AI in Modern Content Creation',
    excerpt: 'Artificial Intelligence is revolutionizing how we create and consume content. This article explores the latest AI tools for content creators and how they\'re reshaping the digital landscape.',
    author: 'Doğukan Yaylacı',
    date: 'November 10, 2024',
    category: 'Technology',
    image: 'https://via.placeholder.com/800x400?text=AI+Content+Creation',
    tags: ['AI', 'Content', 'Technology']
  },
  {
    id: 'web-security-best-practices',
    title: 'Web Security Best Practices for 2025',
    excerpt: 'Cybersecurity threats continue to evolve. Learn the essential web security practices that every developer and IT professional should implement to protect their digital assets in 2025.',
    author: 'Doğukan Yaylacı',
    date: 'October 5, 2024',
    category: 'Security',
    image: 'https://via.placeholder.com/800x400?text=Web+Security',
    tags: ['Security', 'Web Development', 'Cybersecurity']
  },
  {
    id: 'future-of-frontend-development',
    title: 'The Future of Frontend Development',
    excerpt: 'From WebAssembly to AI-assisted coding, discover the emerging technologies and approaches that will define frontend development in the coming years.',
    author: 'Doğukan Yaylacı',
    date: 'September 12, 2024',
    category: 'Development',
    image: 'https://via.placeholder.com/800x400?text=Frontend+Development',
    tags: ['Frontend', 'Development', 'Web Technologies']
  },
  {
    id: 'cloud-native-architecture',
    title: 'Understanding Cloud-Native Architecture',
    excerpt: 'Dive into the principles and benefits of cloud-native architecture and how it enables organizations to build scalable, resilient applications designed specifically for cloud environments.',
    author: 'Doğukan Yaylacı',
    date: 'August 15, 2024',
    category: 'Cloud',
    image: 'https://via.placeholder.com/800x400?text=Cloud+Native',
    tags: ['Cloud', 'Architecture', 'Microservices']
  },
  {
    id: 'responsible-ai-development',
    title: 'Ethical Considerations in AI Development',
    excerpt: 'As AI becomes more prevalent in our daily lives, developers must consider the ethical implications of their work. This article explores responsible AI development practices and guidelines.',
    author: 'Doğukan Yaylacı',
    date: 'July 22, 2024',
    category: 'Technology',
    image: 'https://via.placeholder.com/800x400?text=Ethical+AI',
    tags: ['AI', 'Ethics', 'Technology']
  }
];

const BlogPageContainer = styled.div`
  padding: 120px 50px 80px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 120px 20px 60px;
  }
`;

const PageTitle = styled(motion.h1)`
  font-size: 2.5rem;
  margin-bottom: 50px;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: #000;
  }
`;

const FilterSection = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 40px;
  flex-wrap: wrap;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const TagsFilter = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const TagButton = styled(motion.button)`
  padding: 8px 16px;
  background-color: ${props => props.active ? '#000' : '#f0f0f0'};
  color: ${props => props.active ? '#fff' : '#333'};
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s;
  
  &:hover {
    background-color: ${props => props.active ? '#333' : '#e0e0e0'};
  }
`;

const SearchBox = styled.div`
  position: relative;
  min-width: 250px;
  
  input {
    width: 100%;
    padding: 10px 40px 10px 15px;
    border: 1px solid #ddd;
    border-radius: 20px;
    font-size: 0.9rem;
    
    &:focus {
      outline: none;
      border-color: #999;
    }
  }
  
  svg {
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    color: #666;
  }
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BlogCard = styled(motion(Link))`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0,0,0,0.1);
    
    img {
      transform: scale(1.05);
    }
    
    h3 {
      color: #555;
    }
  }
`;

const BlogImage = styled.div`
  height: 200px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s;
  }
`;

const BlogContent = styled.div`
  padding: 20px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const BlogCategory = styled.span`
  background-color: #f0f0f0;
  color: #666;
  font-size: 0.8rem;
  padding: 4px 10px;
  border-radius: 20px;
  display: inline-block;
  margin-bottom: 10px;
`;

const BlogTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 10px;
  transition: color 0.3s;
`;

const BlogExcerpt = styled.p`
  color: #666;
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 15px;
  flex-grow: 1;
`;

const BlogMeta = styled.div`
  display: flex;
  justify-content: space-between;
  color: #999;
  font-size: 0.8rem;
  border-top: 1px solid #f0f0f0;
  padding-top: 15px;
  
  div {
    display: flex;
    align-items: center;
    
    svg {
      margin-right: 5px;
    }
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 60px;
  gap: 10px;
`;

const PageButton = styled(motion.button)`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: ${props => props.active ? '#000' : '#fff'};
  color: ${props => props.active ? '#fff' : '#333'};
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background-color: ${props => props.active ? '#333' : '#f9f9f9'};
  }
`;

const BlogPage = () => {
  // Sayfaya geldiğinde en üste scroll yapar
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 3;
  
  // Add state for tag filtering and search
  const [activeTag, setActiveTag] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter posts based on tag and search term
  const filteredPosts = blogPosts.filter(post => {
    // Filter by tag (if not "All")
    const matchesTag = activeTag === 'All' || 
                      post.category === activeTag || 
                      post.tags.includes(activeTag);
    
    // Filter by search term
    const matchesSearch = searchTerm === '' || 
                         post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesTag && matchesSearch;
  });
  
  // Get current posts based on pagination with filtered posts
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  
  // Change page handler
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  // Handle tag filter change
  const handleTagChange = (tag) => {
    setActiveTag(tag);
    setCurrentPage(1); // Reset to first page when changing filters
  };
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Gerçek projede bu state'ler ve filtreler API çağrıları ile çalışabilir
  const allTags = ['All', 'Technology', 'Security', 'Development', 'Cloud'];
  
  return (
    <>
      <Helmet>
        <title>Blog - Technology & IT Insights | Dogukan Yaylaci</title>
        <meta name="description" content="Explore insights on IT, cybersecurity, web development, and technology trends. Read articles from Dogukan Yaylaci covering topics like SASE, AI, and cloud architecture." />
        <meta name="keywords" content="IT blog, technology blog, cybersecurity, web development, AI, cloud architecture, SASE, frontend development" />
        <link rel="canonical" href="https://dogukanyaylaci.dev/blog" />
      </Helmet>
      
      <BlogPageContainer>
        <PageTitle
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Blog
        </PageTitle>
        
        <FilterSection>
          <TagsFilter>
            {allTags.map((tag, index) => (
              <TagButton 
                key={index}
                active={tag === activeTag}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleTagChange(tag)}
              >
                {tag}
              </TagButton>
            ))}
          </TagsFilter>
          
          <SearchBox>
            <input 
              type="text" 
              placeholder="Search blogs..." 
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <FontAwesomeIcon icon={faSearch} />
          </SearchBox>
        </FilterSection>
        
        <BlogGrid>
          {currentPosts.length > 0 ? (
            currentPosts.map((post, index) => (
              <BlogCard 
                to={`/blog/${post.id}`} 
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <BlogImage>
                  <img src={post.image} alt={post.title} />
                </BlogImage>
                <BlogContent>
                  <BlogCategory>{post.category}</BlogCategory>
                  <BlogTitle>{post.title}</BlogTitle>
                  <BlogExcerpt>{post.excerpt}</BlogExcerpt>
                  <BlogMeta>
                    <div>
                      <FontAwesomeIcon icon={faUser} />
                      <span>{post.author}</span>
                    </div>
                    <div>
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      <span>{post.date}</span>
                    </div>
                  </BlogMeta>
                </BlogContent>
              </BlogCard>
            ))
          ) : (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '50px 0' }}>
              <h3>No posts found matching your search criteria.</h3>
              <p>Try different keywords or categories.</p>
            </div>
          )}
        </BlogGrid>
        
        {filteredPosts.length > 0 && (
          <Pagination>
            {[...Array(Math.ceil(filteredPosts.length / postsPerPage))].map((_, index) => (
              <PageButton 
                key={index}
                active={currentPage === index + 1}
                whileHover={{ scale: 1.1 }} 
                whileTap={{ scale: 0.9 }}
                onClick={() => handlePageChange(index + 1)}
              >
                {index + 1}
              </PageButton>
            ))}
          </Pagination>
        )}
      </BlogPageContainer>
    </>
  );
};

export default BlogPage; 